// Sugalarm — "tap → hand a meal photo straight to /log/photo" action for nav
// affordances (kiosk side rail, mobile main menu) outside the Log screen
// itself. Mirrors LogScreen's direct-camera / tablet-chooser handoff
// (a hidden <input type="file"> clicked inside the tap gesture, then
// setPendingPhotoFile + nav("/log/photo")) so it lands on PhotoMealScreen at
// the `preview` phase with zero extra clicks — same as every other entry
// point.

import { useCallback, useRef } from "react";
import type { ChangeEvent, RefObject } from "react";
import { useNavigate } from "react-router-dom";

import { ACCEPT_IMAGE_CAMERA } from "../lib/imageAccept";
import { openPhotoInput, photoBlockedByAlarm } from "../lib/nativeCamera";
import { setPendingPhotoFile } from "../lib/photoHandoff";
import { cameraOnlyDevice } from "../lib/photoSource";

// Explicit list for the Android-tablet-kiosk single-chooser input — its
// native KioskWebChromeClient branches purely on `isCaptureEnabled()`, not a
// literal accept-string match, so an explicit list is safe here (and lets
// the native "Take photo / Choose from gallery" dialog still offer a
// gallery option). See lib/imageAccept.ts for why the direct-camera input
// below must stay exactly `image/*` instead.
const ACCEPT_KIOSK_CHOOSER = "image/jpeg,image/png,image/webp,image/heic";

export interface PhotoCaptureAction {
  inputRef: RefObject<HTMLInputElement>;
  accept: string;
  capture?: "environment";
  onChange: (e: ChangeEvent<HTMLInputElement>) => void;
  open: () => void;
}

/**
 * `nativeChooser: true` — no `capture` attribute + an explicit accept list,
 * for the Android-tablet kiosk (its native WebChromeClient turns the plain
 * click into its own in-app camera/gallery dialog).
 *
 * `nativeChooser: false` — `capture="environment"` + `image/*` opens the
 * rear camera directly on web/mobile.
 */
export function usePhotoCaptureAction(
  nativeChooser: boolean,
): PhotoCaptureAction {
  const nav = useNavigate();
  const inputRef = useRef<HTMLInputElement>(null);

  // Cycle 455: a meal photo — meal_photo goes to the kiosk APK right before
  // the click (a silent no-op off the kiosk / on an older APK), and while any
  // alarm sounds on the kiosk the action does nothing (lib/nativeCamera).
  const open = useCallback(() => {
    if (photoBlockedByAlarm()) return;
    openPhotoInput(inputRef.current, "meal_photo");
  }, []);

  const onChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      // Clear so re-picking the same file still fires change.
      e.target.value = "";
      if (!file) return; // chooser cancelled
      setPendingPhotoFile(file);
      nav("/log/photo");
    },
    [nav],
  );

  // Cycle 458: on the kiosk APK (camera-only, lib/photoSource) the chooser
  // input carries `capture` too, so a chooser whose meal_photo purpose never
  // paired routes to the in-app camera and never to the native "Choose from
  // gallery" dialog. The standard phone app on a tablet keeps the
  // capture-less input, which is what reaches its photo picker.
  const capture = !nativeChooser || cameraOnlyDevice() ? "environment" : undefined;

  return {
    inputRef,
    accept: nativeChooser ? ACCEPT_KIOSK_CHOOSER : ACCEPT_IMAGE_CAMERA,
    capture,
    onChange,
    open,
  };
}
